import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import MainCard from "@appsinti/ui-core/MainCard";
import CardTemplate from "@appsinti/ui-core/CardTemplate";
import { Row, FullWidth, CardTitle } from "@appsinti/ui-core/styles";

import useI18n from "@appsinti/i18n/useI18n";
import IVideo from "./interfaces/IVideo";
import { getData } from "./services/cameras";
import { useUpdate } from "@appsinti/trigger-update";
import { Ring } from "@uiball/loaders";
import { getPreviewMirror, updatePreviewMirror } from "@appsinti/preview-mirror";
import CamerasPreview, { isCamerasPreview } from "./interfaces/CamerasPreview";
import { useImagePreload } from "./hooks/useImagePreload";
import { shouldAttemptRecovery } from "./utils/recovery";

const CamerasGrid = styled.div`
  height: 100%;
  display: grid;
  grid-auto-flow: row;
  grid-template-rows: 1fr 1fr;
  grid-row-gap: 5px;
`;

const PreviewImage = styled.div<{ $previewImage: string }>`
  background-image: url(${({ $previewImage }) => $previewImage});
  background-size: cover;
  width: 100%;
  height: 100%;
`;

const PreviewTile = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PlaceholderTile = styled(PreviewTile)`
  background-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
`;

const CameraPreviewImage: React.FC<{
  url: string;
  label: string;
  onLoadError: () => void;
}> = ({ url, label, onLoadError }) => {
  const { status, lastLoadedUrl } = useImagePreload(url);

  useEffect(() => {
    if (status === "failed") onLoadError();
  }, [status]);

  const displayUrl = status === "loaded" ? url : lastLoadedUrl;

  if (displayUrl) {
    return <PreviewImage $previewImage={displayUrl} />;
  }

  if (status === "loading") {
    return (
      <PreviewTile>
        <Ring color={"white"} size={30} />
      </PreviewTile>
    );
  }

  return <PlaceholderTile>{label}</PlaceholderTile>;
};

const Preview: React.FC<{ appId: number }> = ({ appId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { translate } = useI18n();
  const [previewCameras, setPreviewCameras] = useState<IVideo[] | CamerasPreview | null>(null);
  const { translateObject } = useI18n();
  const { update, addLoadingState, resolveLoadingState } = useUpdate();
  const lastPreviewRecoveryRef = useRef<number | null>(null);

  async function fetchPreviewCameras(appId: number): Promise<CamerasPreview | null> {
    try {
      setLoading(true);
      addLoadingState(`bigbrother-frontend-${appId}`);
      const { previews } = await getData(appId);

      const data: CamerasPreview = {
        feeds: previews,
        lastUpdate: new Date().getTime(),
      };
      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      resolveLoadingState(`bigbrother-frontend-${appId}`);
    }
    return null;
  }

  const storeLocalPreview = async () => {
    if(!appId) return;
    const previewData = await fetchPreviewCameras(appId);
    setPreviewCameras(previewData);
    if(previewData) {
      updatePreviewMirror(JSON.stringify(previewData), appId);
    }
}

  // A failed preview may just carry an expired signature: refetch once
  // (cooldown-guarded) so fresh signed URLs can heal it.
  const handlePreviewLoadError = () => {
    const now = Date.now();
    if (!shouldAttemptRecovery(lastPreviewRecoveryRef.current, now)) return;
    lastPreviewRecoveryRef.current = now;
    storeLocalPreview();
  };

  const getLocalPreview = (appId: number) => {
    const preview = getPreviewMirror(appId);
    if (!preview) { 
      storeLocalPreview();
    }
    else {
      setPreviewCameras(JSON.parse(preview) as IVideo[] | CamerasPreview);
    }
  }

  useEffect(() => {
    if(!appId) return;
    getLocalPreview(appId);
  }, [appId]);

  useEffect(() => {
    storeLocalPreview();
  }, [update]);

  const lastUpdatedTime = useMemo(() => {
    if (previewCameras && isCamerasPreview(previewCameras) && previewCameras.lastUpdate) {
      return new Date(previewCameras.lastUpdate);
    }
  }, [previewCameras]);

  const previewCamerasArray = useMemo(() => {
    if (isCamerasPreview(previewCameras)) {
      return previewCameras.feeds;
    }
    if(Array.isArray(previewCameras)) {
      return previewCameras;
    }
    return [];
  }, [previewCameras]);

  return (
    <MainCard title={translate("Cameras")} lastUpdatedTime={lastUpdatedTime}>
      <CamerasGrid>
        {loading && previewCamerasArray.length === 0 ? (
          <div style={{ justifySelf: "center" }}>
            <Ring color={"white"} size={30} />
          </div>
        ) : (
          previewCamerasArray.map((camera) => (
            <CardTemplate
              header={
                <Row>
                  <FullWidth>
                    <CardTitle>
                      {translateObject(camera.locationName)}
                    </CardTitle>
                  </FullWidth>
                </Row>
              }
              body={
                <CameraPreviewImage
                  url={camera.previewImageURL}
                  label={translateObject({ en: "No preview", es: "Sin vista previa" })}
                  onLoadError={handlePreviewLoadError}
                />
              }
              noPadding={true}
              key={camera.id}
            />
          ))
        )}
      </CamerasGrid>
    </MainCard>
  );
};

export default Preview;
